import { useEffect } from 'react';
import { Outlet, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/store';
import { useRestaurantStore } from '../store';
import { restaurantApi } from '../api';
import { mapRestaurantResponseToRestaurant } from '../types';
import type { RestaurantResponse } from '../types';
import { useRestaurantLanguage } from '@/shared/hooks/useRestaurantLanguage';

/**
 * Thin layout wrapper for restaurant-scoped routes. Loads the active restaurant
 * into the query cache + Zustand store so deeper pages have it ready, then
 * renders the nested route.
 *
 * The actual UI shell (top bar, sidebar) lives in `AppShell` — this layout no
 * longer renders chrome.
 */
export function RestaurantLayout() {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = useAuthStore((s) => s.token);
  const setCurrentRestaurant = useRestaurantStore((s) => s.setCurrentRestaurant);

  const { data: restaurant } = useQuery<RestaurantResponse>({
    queryKey: ['restaurant', restaurantId],
    queryFn: () => restaurantApi.getRestaurant(token, restaurantId!),
    enabled: !!token && !!restaurantId,
  });

  // Switch the admin UI language to match the restaurant's defaultLanguage.
  // Restores the previous language when the user navigates away from this restaurant.
  useRestaurantLanguage(restaurant?.defaultLanguage);

  useEffect(() => {
    if (restaurant) {
      setCurrentRestaurant(mapRestaurantResponseToRestaurant(restaurant));
    }
  }, [restaurant, setCurrentRestaurant]);

  // Strip the `?new=true` flag once the restaurant has been loaded — it's only
  // used by the create-restaurant flow to indicate a fresh redirect.
  useEffect(() => {
    if (searchParams.get('new') === 'true' && restaurant) {
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, restaurant, setSearchParams]);

  return <Outlet />;
}
